// Cloud-TTS-backed voice output. Same speak()/stop() shape as VoiceOutput so
// callers can swap implementations without changes. Falls back per-utterance
// to the legacy Web Speech VoiceOutput when the server call fails — this
// keeps voice working through transient network blips without permanently
// degrading the session.
//
// On success: server returns base64 MP3, we decode → Blob → Audio → play.
//
// iOS audio unlock
// ----------------
// iOS Safari refuses to play any audio that wasn't initiated from a direct
// user gesture. We work around this with the standard pattern: on the first
// touchstart/click anywhere in the app, play a tiny silent WAV synchronously
// inside the gesture handler. Once that succeeds, every subsequent
// programmatic Audio.play() in the session is allowed.
//
// State is module-level so the flag is shared across every VoiceOutputTTS
// instance — once unlocked, the whole session is unlocked. Speak() called
// before the unlock waits on the unlock promise rather than failing.

import { synthesizeTts } from '../api';
import { VoiceOutput } from './voiceOutput';

// Friendly voice picker values (stored in children.voice_name) → Neural2 voice.
// Default lives at the call site, not here, so unmapped values pass through
// to the server which applies its own default.
const VOICE_MAP = {
  'Flo (English (United Kingdom))':   'en-GB-Neural2-A',
  'Sandy (English (United Kingdom))': 'en-GB-Neural2-C',
  'Daniel':                           'en-GB-Neural2-B',
  'Reed (English (United Kingdom))':  'en-GB-Neural2-D',
};

export const DEFAULT_NEURAL2_VOICE = 'en-GB-Neural2-C';

export function mapToNeural2(voiceName) {
  if (!voiceName) return DEFAULT_NEURAL2_VOICE;
  return VOICE_MAP[voiceName] || DEFAULT_NEURAL2_VOICE;
}

function base64ToBlob(base64, mimeType) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

// ─── iOS unlock plumbing ──────────────────────────────────────────────────────

// Same-origin silent WAV — covered by `media-src 'self'` so no CSP change.
const UNLOCK_AUDIO_URL = '/silence.wav';

let _audioUnlocked = false;
const _unlockWaiters = [];

/** Has the audio session been unlocked by a prior user gesture? */
export function isAudioUnlocked() {
  return _audioUnlocked;
}

/**
 * Plays a silent WAV synchronously to unlock the iOS audio session. Must be
 * called from inside a user-gesture handler (touchstart, click) — on every
 * other platform this is a no-op success. Safe to call multiple times; only
 * the first call does any work.
 */
export function unlockAudio() {
  if (_audioUnlocked) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      _audioUnlocked = true;
      // Drain anyone waiting on the unlock — speak() calls that came in
      // before the gesture each have a resolver in this list.
      while (_unlockWaiters.length > 0) {
        const w = _unlockWaiters.shift();
        try { w(); } catch { /* ignore */ }
      }
      resolve();
    };

    try {
      const a = new Audio(UNLOCK_AUDIO_URL);
      a.muted = true;
      // Best-effort cleanup so we don't leak the element after the gesture.
      a.onended = () => { try { a.src = ''; } catch { /* ignore */ } };

      const playPromise = a.play();
      if (playPromise && typeof playPromise.then === 'function') {
        // Mark unlocked even if the silent play() rejects — leaving the flag
        // false would block every future speak() forever, and rejections
        // here are dominated by autoplay quirks rather than missing gesture.
        playPromise.then(finish).catch(finish);
      } else {
        finish();
      }
    } catch {
      finish();
    }
  });
}

function waitForUnlock() {
  if (_audioUnlocked) return Promise.resolve();
  return new Promise((resolve) => _unlockWaiters.push(resolve));
}

// ─── VoiceOutputTTS ───────────────────────────────────────────────────────────

export class VoiceOutputTTS {
  constructor() {
    this._audio    = null;
    this._objectUrl = null;
    this._fallback = new VoiceOutput();
  }

  // HTML Audio is universally supported in browsers we care about. Even when
  // the API call fails the fallback runs its own isSupported() check, so we
  // can safely return true here unconditionally.
  isSupported() {
    return true;
  }

  // Resolves when playback ends OR fallback playback ends. Rejects only if
  // both the API and the fallback fail — Chat.jsx swallows speak() errors,
  // so a rejection just means no audio for that turn.
  async speak(text, options = {}) {
    this.stop();

    if (!text || !text.trim()) return;

    // iOS gate: hold here until the first user gesture has unlocked audio.
    // On other browsers _audioUnlocked is set by the same listener so this
    // resolves synchronously after the first interaction.
    if (!_audioUnlocked) await waitForUnlock();

    const neural2Voice = mapToNeural2(options.voiceName);

    let response;
    try {
      response = await synthesizeTts(text, neural2Voice);
    } catch {
      return this._fallback.speak(text, options);
    }

    const audio = response?.data?.audio;
    if (!audio) {
      return this._fallback.speak(text, options);
    }

    return new Promise((resolve, reject) => {
      try {
        const blob = base64ToBlob(audio, 'audio/mpeg');
        const url  = URL.createObjectURL(blob);
        const el   = new Audio(url);

        const cleanup = () => {
          if (this._objectUrl === url) {
            URL.revokeObjectURL(url);
            this._objectUrl = null;
          }
          if (this._audio === el) this._audio = null;
        };

        el.onended  = () => { cleanup(); resolve(); };
        el.onerror  = () => { cleanup(); reject(new Error('Audio playback failed')); };

        this._audio    = el;
        this._objectUrl = url;
        el.play().catch((err) => { cleanup(); reject(err); });
      } catch (err) {
        reject(err);
      }
    }).catch(() => this._fallback.speak(text, options));
  }

  stop() {
    if (this._audio) {
      try { this._audio.pause(); } catch { /* ignore */ }
      try { this._audio.currentTime = 0; } catch { /* ignore */ }
    }
    if (this._objectUrl) {
      try { URL.revokeObjectURL(this._objectUrl); } catch { /* ignore */ }
      this._objectUrl = null;
    }
    this._audio = null;
    this._fallback.stop();
  }
}
